import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, UserCog, AlertTriangle, Package, Settings, Upload, Wrench } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ImportarClientesDialog } from "@/components/clientes/ImportarClientesDialog";
import { ImportarUsuariosDialog } from "@/components/usuarios/ImportarUsuariosDialog";
import { ImportarCodigosAnomaliaDialog } from "@/components/anomalias/ImportarCodigosAnomaliaDialog";
import { ImportarCatalogoDialog } from "@/components/inventario/ImportarCatalogoDialog";

const importaciones = [
  {
    id: "clientes",
    title: "Importar Clientes",
    description: "Carga masiva de clientes desde Excel o CSV con detección de duplicados por NIF/CIF.",
    icon: Users,
    color: "text-[hsl(var(--raven-violet))]",
  },
  {
    id: "usuarios",
    title: "Importar Usuarios",
    description: "Alta masiva de técnicos, gestores y administradores desde una plantilla Excel.",
    icon: UserCog,
    color: "text-[hsl(var(--raven-ice))]",
  },
  {
    id: "anomalias",
    title: "Importar Códigos de Anomalías",
    description: "Carga de códigos de anomalías organizados por tipo de sistema y severidad.",
    icon: AlertTriangle,
    color: "text-yellow-400",
  },
  {
    id: "catalogo",
    title: "Importar Catálogo de Equipos",
    description: "Importa marcas, modelos y tipos de equipo desde una plantilla Excel.",
    icon: Package,
    color: "text-orange-400",
  },
] as const;

type DialogId = (typeof importaciones)[number]["id"];

const Herramientas = () => {
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState<DialogId | null>(null);

  return (
    <div className="p-4 md:p-6 space-y-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10">
          <Wrench className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Herramientas</h1>
          <p className="text-muted-foreground text-sm">
            Importaciones masivas, plantillas y configuración de empresa
          </p>
        </div>
      </div>

      {/* Importaciones Masivas */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Upload className="h-5 w-5 text-muted-foreground" />
          Importaciones Masivas
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {importaciones.map((item) => (
            <Card key={item.id} className="group hover:border-primary/40 transition-colors">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-md bg-muted">
                    <item.icon className={`h-5 w-5 ${item.color}`} />
                  </div>
                  <div>
                    <CardTitle className="text-base">{item.title}</CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <CardDescription className="text-xs leading-relaxed">
                  {item.description}
                </CardDescription>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  onClick={() => setOpenDialog(item.id)}
                >
                  <Upload className="h-4 w-4 mr-1" />
                  Importar
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Configuración */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Settings className="h-5 w-5 text-muted-foreground" />
          Configuración
        </h2>
        <Card
          className="cursor-pointer hover:border-primary/40 transition-colors"
          onClick={() => navigate("/configuracion-empresa")}
        >
          <CardHeader className="pb-2">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-md bg-muted">
                <Settings className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <CardTitle className="text-base">Configuración de Empresa</CardTitle>
                <CardDescription className="text-xs">
                  Logo, datos fiscales, dirección y ajustes generales
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>
      </section>

      {/* Dialogs */}
      <ImportarClientesDialog
        open={openDialog === "clientes"}
        onOpenChange={(open) => !open && setOpenDialog(null)}
        onSuccess={() => setOpenDialog(null)}
      />
      <ImportarUsuariosDialog
        open={openDialog === "usuarios"}
        onOpenChange={(open) => !open && setOpenDialog(null)}
        onSuccess={() => setOpenDialog(null)}
      />
      <ImportarCodigosAnomaliaDialog
        open={openDialog === "anomalias"}
        onOpenChange={(open) => !open && setOpenDialog(null)}
        onSuccess={() => setOpenDialog(null)}
      />
      <ImportarCatalogoDialog
        open={openDialog === "catalogo"}
        onOpenChange={(open) => !open && setOpenDialog(null)}
        onSuccess={() => setOpenDialog(null)}
      />
    </div>
  );
};

export default Herramientas;
